import { loadGoogleFont } from "utils/googleFonts";
import "resources/styles/template4.css";
import { Form, FormInstance } from "antd";
import { useEffect } from "react";
import useGetLogo from "../hooks/useGetLogo";
import { sanitizeHtml } from "utils/sanitizeHtml";

interface FontOption {
  value: unknown;
  label: unknown;
}

export default function Template4({
  form,
  newFontsOptions,
  companyData,
  companyBankAccount: _companyBankAccount,
}: {
  form: FormInstance;
  newFontsOptions: FontOption[];
  companyBankAccount?: BankAccountResult;
  companyData: {
    company: GetCompanyWithAddressCompany;
    address: AddressResult;
  };
}): React.ReactElement {
  const logoDescription = Form.useWatch(["logoDescription"], form);
  const tableBody = Form.useWatch(["tableBorder"], form);
  const tableHeaderColor = Form.useWatch(["baseTextColorBackground"], form);
  const watermarkOpacity = Form.useWatch(["backgroundImageOpacity"], form);
  const baseColor = Form.useWatch(["baseColor"], form);
  const baseTextColor = Form.useWatch(["baseTextColor"], form);
  const titleColor = Form.useWatch(["titleColor"], form);
  const font = Form.useWatch(["font"], form);
  const showName = Form.useWatch(["showName"], form);
  const showDescription = Form.useWatch(["showDescription"], form);
  const showStampSpace = Form.useWatch(["showStampSpace"], form);
  const showMentionAndLegalStatus = Form.useWatch(["showMentionAndLegalStatus"], form);
  const logo = Form.useWatch(["logo"], form);
  const hasLogo = logo?.file?.status !== "uploading";
  const note = Form.useWatch(["note"], form);
  const latePaymentPenalty = Form.useWatch(["latePaymentPenalty"], form);
  const fixedRecoveryFee = Form.useWatch(["fixedRecoveryFee"], form);
  const discount = Form.useWatch(["discount"], form);
  const backgroundImage = Form.useWatch(["backgroundImage"], form);
  const showDeliveryAddress = Form.useWatch(["showDeliveryAddress"], form);
  const logoPosition = Form.useWatch(["logoPosition"], form);
  const fontName = (
    newFontsOptions?.find((item: FontOption) => item?.value === font)?.label as
      | { props?: { children?: string } }
      | undefined
  )?.props?.children;

  const address = companyData?.address;
  const company = companyData?.company;
  const isIntreprenneur = company?.legalCategory?.code === "1000";
  const { generateByLogo, accountingFirmName } = useGetLogo();

  useEffect(() => {
    if (fontName) {
      loadGoogleFont(fontName);
    }
  }, [fontName]);

  const fontStyle = fontName ? { fontFamily: `"${fontName}", sans-serif` } : {};

  return (
    <div className={`A4Container4 logo-position4-${logoPosition || "left"}`} style={fontStyle}>
      {backgroundImage && (
        <div
          className="background-image4"
          style={{
            opacity: watermarkOpacity || 0.05,
          }}
        >
          <img src={backgroundImage} alt="" />
        </div>
      )}
      {/* Header */}
      <header className="header-section4" style={{ borderBottomColor: baseColor || "#e5e7eb" }}>
        <div className="logo-area4">
          {hasLogo ? (
            <img src={logo} alt="" />
          ) : (
            <div className="logo-placeholder" style={{ borderColor: baseColor || "#e0e0e0" }}>
              <span style={{ color: baseTextColor || "#000" }}>LOGO</span>
            </div>
          )}
          <div className="logo-description4" style={{ color: titleColor || "#000", ...fontStyle }}>
            {logoDescription}
          </div>
        </div>
        <div className="invoice-info4">
          <h1 className="invoice-title4" style={{ color: titleColor || "#000", ...fontStyle }}>
            FACTURE
          </h1>
          <p className="invoice-subtitle4" style={{ color: titleColor || "#000", ...fontStyle }}>
            <b>Facture n°:</b> F-123456789
          </p>
          <p className="invoice-subtitle4" style={{ color: titleColor || "#000", ...fontStyle }}>
            <b>Date:</b> {new Date().toLocaleDateString("fr-FR")}
          </p>
        </div>
      </header>

      {/* Two Columns */}
      <div className="two-columns4" style={{ color: baseTextColor || "#000" }}>
        <div className="column4">
          <div className="column-content4">
            <address>
              <b>{company?.displayName || company?.name}</b>
              <span>{address?.address1 || ""}</span>
              <span>{address?.address2 || ""}</span>
              <span>
                {address?.postalCode || ""} {address?.city || ""} {address?.country?.name || ""}
              </span>
              <span>{company?.email || ""}</span>
              <span>{company?.phone1 || ""}</span>
            </address>
          </div>

          <div
            className="info-box4 company-info"
            style={{
              borderColor: baseColor || "#000",
              backgroundColor: baseColor ? `${baseColor}30` : "#f8f9fa",
            }}
          >
            <div className="column-content4">
              <div className="info-row4">
                <span className="info-label4">Date:</span>
                <span className="info-value4">{new Date().toLocaleDateString("fr-FR")}</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">Échéance:</span>
                <span className="info-value4">
                  {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString("fr-FR")}
                </span>
              </div>
              <div className="info-row4">
                <span className="info-label4">TVA Intracom:</span>
                <span className="info-value4">{company?.euVatNumber || "—"}</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">SIRET:</span>
                <span className="info-value4">{company?.vatNumber || "—"}</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">CODE APE:</span>
                <span className="info-value4">{company?.businessActivity?.code || "—"}</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">FORME JURIDIQUE:</span>
                <span className="info-value4">{company?.legalStructure?.code || "—"}</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">RCS:</span>
                <span className="info-value4">{company?.rcs || "—"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="column4">
          <div className="column-content4">
            <address>
              <b>ACME CORPORATION - ENTREPÔT</b>
              <span>789 Avenue de la Logistique</span>
              <span>Zone Industrielle</span>
              <span>75019 Paris, France</span>
              <span>contact@acme-corp.com</span>
              <span>+33 1 23 45 67 89</span>
            </address>
            {showDeliveryAddress && (
              <>
                <address>
                  <b style={{ color: titleColor || "#1e293b" }}>Adresse de livraison:</b>
                  <span>ACME CORPORATION - LIVRAISON</span>
                  <span>456 Rue de la Distribution</span>
                  <span>Quartier Commercial</span>
                  <span>75008 Paris, France</span>
                </address>
              </>
            )}
          </div>

          <div
            className="info-box4"
            style={{
              borderColor: baseColor || "#000",
              backgroundColor: baseColor ? `${baseColor}30` : "#f8f9fa",
            }}
          >
            <div className="column-content4">
              <div className="info-row4">
                <span className="info-label4">N° Client:</span>
                <span className="info-value4">CLI-789123456</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">TVA Intracom:</span>
                <span className="info-value4">FR12345678901</span>
              </div>
              <div className="info-row4">
                <span className="info-label4">SIRET:</span>
                <span className="info-value4">12345678901234</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Extra Info */}
      {(showName || showDescription) && (
        <div className={`extra-info4 ${showName && showDescription ? "compact" : ""}`}>
          {showName && (
            <div className="extra-name4" style={{ color: baseTextColor || "#000" }}>
              Refonte site web e-commerce
            </div>
          )}
          {showDescription && (
            <div className="extra-description4" style={{ color: baseTextColor || "#000" }}>
              Développement complet d&apos;une plateforme e-commerce avec système de paiement
              intégré et interface d&apos;administration avancée
            </div>
          )}
        </div>
      )}

      <div className={`default-table4 body-${tableBody || "border_none"}`}>
        <table
          style={{
            borderColor: baseColor || "#000",
          }}
        >
          <thead
            id="customColumnHeader"
            className="default-table__thead4"
            style={{
              backgroundColor: tableHeaderColor || "#ddd",
              color: titleColor || "#000",
              borderColor: baseColor || "#000",
            }}
          >
            <tr style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="default-table__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>REF</b>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>DÉSIGNATION</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>PU HT</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>QTÉ</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>UNITÉ</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>MONTANT HT</b>
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <b>TVA %</b>
              </td>
            </tr>
          </thead>
          <tbody style={{ color: baseTextColor || "#000" }}>
            <tr className="row" style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="line-description default-default__lines__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <span>SERV-001</span>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                Consultation développement web
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                150.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                8
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                HEURE
              </td>
              <td
                id="td-vatPct"
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                1,200.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                20.00%
              </td>
            </tr>
            <tr className="row" style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="line-description default-default__lines__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <span>PROD-002</span>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                Ordinateur portable professionnel
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                899.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                2
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                UNITAIRE
              </td>
              <td
                id="td-vatPct"
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                1,798.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                20.00%
              </td>
            </tr>
            <tr className="row" style={{ borderColor: baseColor || "#000" }}>
              <td
                colSpan={1}
                className="line-description default-default__lines__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                <span>LIC-003</span>
              </td>
              <td
                colSpan={3}
                className="default-table__column-left4"
                style={{ borderColor: baseColor || "#000" }}
              >
                Licence logiciel annuelle
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                450.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                1
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                UNITAIRE
              </td>
              <td
                id="td-vatPct"
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                450.00
              </td>
              <td
                colSpan={1}
                className="default-table__column-right4"
                style={{ borderColor: baseColor || "#000" }}
              >
                20.00%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <br />
      <div className="totals-container4">
        <div className="totals-left-content4">
          {/* Legal Notice and Notes */}
          {(showMentionAndLegalStatus || note) && (
            <div style={{ marginBottom: "4mm" }}>
              {showMentionAndLegalStatus && (
                <div
                  className="legal-notice4"
                  style={{ color: baseTextColor || "#666", borderLeftColor: baseColor || "#000" }}
                >
                  Article L441-6 du code du commerce, tout retard de paiement donnera lieu à
                  l&apos;application d&apos;une pénalité égale à trois fois le taux d&apos;intérêt
                  légal. Une indemnité forfaitaire pour frais de recouvrement de 40 € sera
                  appliquée.
                </div>
              )}
              {note && (
                <div className="note-section4" style={{ color: baseTextColor || "#333" }}>
                  <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(note) }} />
                </div>
              )}
              <div className="extra-terms-container4">
                {latePaymentPenalty && (
                  <div className="extra-terms4">
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(latePaymentPenalty || "") }} />
                  </div>
                )}
                {fixedRecoveryFee && (
                  <div className="extra-terms4">
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(fixedRecoveryFee || "") }} />
                  </div>
                )}
                {discount && (
                  <div className="extra-terms4">
                    <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(discount || "") }} />
                  </div>
                )}
                {isIntreprenneur && (
                  <div className="extra-terms4">
                    <span>TVA non applicable, article 293 B du CGI.</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="totals-right-section4">
          <div
            className="totals-box4"
            style={{
              borderColor: baseColor || "#000",
              color: baseTextColor || "#000",
            }}
          >
            <div
              className="totals-row4"
              style={{ borderBottomColor: baseColor ? `${baseColor}40` : "#00000040" }}
            >
              <span className="totals-label4">Total HT</span>
              <span className="totals-value4">3,448.00 €</span>
            </div>
            <div
              className="totals-row4"
              style={{ borderBottomColor: baseColor ? `${baseColor}40` : "#00000040" }}
            >
              <span className="totals-label4">Total HT NET</span>
              <span className="totals-value4">3,448.00 €</span>
            </div>
            <div
              className="totals-row4"
              style={{ borderBottomColor: baseColor ? `${baseColor}40` : "#00000040" }}
            >
              <span className="totals-label4">TVA 20%</span>
              <span className="totals-value4">689.60 €</span>
            </div>
            <div className="totals-row4" style={{ borderTopColor: baseColor || "#000" }}>
              <span className="totals-label4">Total TTC</span>
              <span className="totals-value4">4,137.60 €</span>
            </div>
          </div>
          {showStampSpace && (
            <div
              className="stamp-area4"
              style={{
                borderColor: baseColor || "#000",
                marginTop: "3mm",
                color: baseTextColor || "#000",
              }}
            >
              <span className="stamp-text4">Cachet et signature</span>
            </div>
          )}
        </div>
      </div>

      <footer
        className="absolute-footer4"
        style={{
          borderTopColor: baseColor || "#000",
          color: baseTextColor ? `${baseTextColor}20` : "#000",
        }}
      >
        <div className="footer-branding4">
          <span>Document généré par</span>
          <strong>NeoFact</strong>
          <span>by</span>
          <img
            src={generateByLogo ?? undefined}
            alt={accountingFirmName ?? undefined}
            className="newrai-logo4"
          />
        </div>
        <div
          className="footer-content4"
          style={{
            borderTopColor: baseColor || "#e5e7eb",
            color: baseTextColor || "#000",
          }}
        >
          <div className="footer-column4">
            <h3
              className="footer-title4"
              style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
            >
              Données société
            </h3>
            <div className="footer-value4">{company?.displayName || company?.name}</div>
            <div className="footer-value4">{company?.legalStructure?.code || "—"}</div>
            <div className="footer-value4">{company?.vatNumber || "—"}</div>
            <div className="footer-value4">{company?.euVatNumber || "—"}</div>
            <div className="footer-value4">{company?.businessActivity?.code || "—"}</div>
          </div>

          {/* Coordinates Column - Values Only */}
          <div className="footer-column4">
            <h3
              className="footer-title4"
              style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
            >
              Coordonnées
            </h3>
            <div className="footer-value4">
              {address?.address1}
              {address?.address2 && (
                <>
                  <br />
                  {address.address2}
                </>
              )}
              <br />
              {address?.postalCode} {address?.city}
              <br />
              {address?.country?.name}
            </div>
            <div className="footer-value4">{company?.email || "—"}</div>
            <div className="footer-value4">{company?.phone1 || "—"}</div>
            <div className="footer-value4">{company?.website || "—"}</div>
          </div>

          {/* Bank Details Column - Labels and Values on Same Line */}
          <div className="footer-column4">
            <h3
              className="footer-title4"
              style={{ borderBottomColor: baseColor || "#000", color: titleColor || "#000" }}
            >
              Détails bancaires
            </h3>
            <div className="footer-item4-inline">
              <span className="footer-label4">Banque:</span>
              <span className="footer-value4">Crédit Agricole Provence Côte d'Azur</span>
            </div>
            <div className="footer-item4-inline">
              <span className="footer-label4">BIC:</span>
              <span className="footer-value4">AGRIFRPP881</span>
            </div>
            <div className="footer-item4-inline">
              <span className="footer-label4">IBAN:</span>
              <span className="footer-value4">FR76 1881 9000 1234 5678 9012 345</span>
            </div>
            <div className="footer-item4-inline">
              <span className="footer-label4">Paiement:</span>
              <span className="footer-value4">Virement bancaire</span>
            </div>
            <div className="footer-item4-inline">
              <span className="footer-label4">Conditions:</span>
              <span className="footer-value4">30 jours net</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
